import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const API_URL = '/api'; //'http://localhost:8002/api'

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // 자동 로그인 처리 함수 추가
    const handleAutoLogin = async (token) => {
        try {
            // 자동 로그인 API 호출
            const response = await axios.get(`${API_URL}/auth/auto`, {
                params: { token }
            });

            if (response.data.success) {
                // 토큰 저장 및 사용자 정보 설정
                localStorage.setItem('auth_token', response.data.token);
                setIsAuthenticated(true);
                setUser({ user_id: response.data.user_id });
                return true;
            }
        } catch (error) {
            console.error('Auto login failed:', error);
        }
        return false;
    };

    // Telegram Mini App 자동 로그인.
    // 채팅 안에서 열리면 Telegram 이 initData 를 넘겨준다. 서버가 봇 토큰으로 HMAC 을
    // 검증하므로 그 자체가 신원 증명이고, 별도 로그인 절차가 필요 없다.
    const handleMiniAppLogin = async () => {
        const tg = window.Telegram?.WebApp;
        const initData = tg?.initData;
        if (!initData) return false;

        try {
            tg.ready();
            tg.expand();
        } catch (e) {
            // SDK 가 있어도 구버전이면 일부 메서드가 없을 수 있다. 로그인과 무관하다.
        }

        try {
            const response = await axios.post(`${API_URL}/telegram/miniapp/auth`, { initData });
            localStorage.setItem('auth_token', response.data.token);
            setIsAuthenticated(true);
            setUser({ user_id: response.data.user_id });
            return true;
        } catch (error) {
            // 초대받지 않은 사용자(403)면 일반 로그인 화면으로 떨어뜨린다
            console.error('Mini App login failed:', error);
            return false;
        }
    };

    useEffect(() => {
        const checkAuth = async () => {
            setLoading(true);

            // Telegram 채팅 안에서 열린 경우 — 다른 어떤 경로보다 먼저 시도한다
            if (window.Telegram?.WebApp?.initData) {
                const success = await handleMiniAppLogin();
                if (success) {
                    setLoading(false);
                    return;
                }
            }

            // URL에서 토큰 파라미터 확인
            const params = new URLSearchParams(window.location.search);
            const autoLoginToken = params.get('token');

            // 자동 로그인 토큰이 있으면 처리
            if (autoLoginToken && window.location.pathname.includes('/auth/auto')) {
                const success = await handleAutoLogin(autoLoginToken);

                // 성공하면 홈으로 리다이렉트
                if (success) {
                    window.history.replaceState({}, document.title, '/');
                    setLoading(false);
                    return;
                }
            }

            // 기존 토큰 확인 로직
            const token = localStorage.getItem('auth_token');
            if (token) {
                try {
                    const response = await axios.get(`${API_URL}/auth/validate`, {
                        params: { token }
                    });
                    if (response.data.valid) {
                        setIsAuthenticated(true);
                        setUser({ user_id: response.data.user_id });
                    } else {
                        await logout();
                    }
                } catch (error) {
                    await logout();
                }
            }
            setLoading(false);
        };

        checkAuth();
    }, []);

    const login = async (token, user_id) => {
        localStorage.setItem('auth_token', token);
        setIsAuthenticated(true);
        setUser({ user_id });
    };

    const logout = async () => {
        localStorage.removeItem('auth_token');
        setIsAuthenticated(false);
        setUser(null);
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            user,
            login,
            logout,
            loading
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);